/* eslint-disable camelcase */
import React, { useMemo } from "react"
import types from "prop-types"
import DOMPurify from "dompurify"
import { isEqual } from "lodash-es"
import { useSelector } from "react-redux"
import { getClockFormat } from "reduxSlices/currentSettingsSlice"
import { organizationUsersById } from "reduxSlices/organizationUsersSlice"
import { getTaskUserIds } from "reduxSlices/formSubmissionSlice"
import { taskShape, questionShape, userOptionShape } from "utils/propTypeShapes"
import { orgLocalDateTimeByClockFormat } from "utils/dateHelpers"
import { escapeHtml } from "utils/stringHelpers"
import { ProfileIcon } from "shared/icons"
import SubformAnswerEntryMenu from "./SubformAnswerEntryMenu"

// Entry text should match the @ followed by the user's full name.
// To avoid matching a name contained within another name
// (e.g., Joe Roberts within Joe Robertson), the full name should be followed
// by a non-letter or the end of a line to signify the end of the mentioned name.
const mentionRegExp = (names) => new RegExp(`@(${names.join("|")})(?=[^a-z]|$)`, "gi")

const highlightMentions = (text, matcher) => (
  text.replaceAll(matcher, "<span class=\"mention\">$&</span>")
)

const SubformAnswerEntryCard = ({ task, question, entry }) => {
  const clockFormat = useSelector(getClockFormat)

  const taskUserIds = useSelector(getTaskUserIds(task.id), isEqual)
  const taskUsers = useSelector(organizationUsersById(taskUserIds), isEqual)
  const mentionableNames = useMemo(() => taskUsers.map((taskUser) => taskUser.fullName), [taskUsers])
  const mentionMatcher = useMemo(() => mentionRegExp(mentionableNames), [mentionableNames])

  const { answer, created_at, user } = entry

  const answerWithHighlightedMentions = useMemo(() => (
    DOMPurify.sanitize(highlightMentions(escapeHtml(answer), mentionMatcher), { USE_PROFILES: { html: true } })
  ), [answer, mentionMatcher])

  return (
    <div className="flex lg:rounded-lg bg-light-100 border-b lg:border lg:border-lightgray-3 last:border-b-0 lg:last:border-b mb-6 lg:p-3 pb-6 last:pb-0 lg:last:pb-3">
      <ProfileIcon className="hidden lg:flex shrink-0 mr-3" user={user} />
      <div className="grow">
        <div className="grid grid-cols-1-auto lg:gap-4 w-full mb-2">
          <span className="flex flex-col lg:flex-row lg:justify-between screenreader-whitespace">
            <span className="grow mr-4 font-semibold text-sm break-anywhere">
              { user.fullName }
            </span>
            <span className="shrink-0 text-xs lg:text-sm text-primary-300">
              { orgLocalDateTimeByClockFormat(created_at, clockFormat) }
            </span>
          </span>
          <SubformAnswerEntryMenu
            className="shrink-0"
            task={task}
            question={question}
            entry={entry}
          />
        </div>
        <div
          className="whitespace-pre-wrap break-anywhere"
          dangerouslySetInnerHTML={{ __html: answerWithHighlightedMentions.trim() }} // eslint-disable-line react/no-danger
        />
      </div>
    </div>
  )
}

SubformAnswerEntryCard.propTypes = {
  task: taskShape.isRequired,
  question: questionShape.isRequired,
  entry: types.shape({
    answer: types.string.isRequired,
    created_at: types.string.isRequired,
    uuid: types.string.isRequired,
    user: userOptionShape.isRequired,
  }).isRequired,
}

export default SubformAnswerEntryCard
